/* eslint-disable jsx-a11y/no-autofocus -- Preserve focus on the sole code-entry control from the legacy OTP component. */
import { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'
import { Auth } from 'aws-amplify'
import Icon from '@/components/Icon'
import { REGEXP_ONLY_DIGITS } from 'input-otp'

import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/InputOTP'
import { useTranslation } from 'react-i18next'

import DrkTrimMainLogo from '@/components/svg/DrkTrimMain'

import { Alert } from '@/actions'

// scss files
import appScss from '../App/App.scss'
import scss from './Login.scss'

export default function ResetPassword(props) {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const [isSigningIn, setIsSigningIn] = useState(false)

  const [confirmationCode, setConfirmationCode] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  const [codeSent, setCodeSent] = useState(false)
  const [confirmed, setConfirmed] = useState(false)

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const loginPath = email
    ? `/login?email=${encodeURIComponent(email.trim().toLowerCase())}`
    : '/login'

  useEffect(() => {
    document.getElementsByTagName('body')[0].className = scss.boxedFormPage
    return () => {
      document.getElementsByTagName('body')[0].className = ''
    }
  }, [])

  const validateCodeForm = email?.length > 0

  const validateResetForm =
    confirmationCode?.length > 0 &&
    password?.length > 0 &&
    password === confirmPassword

  const handleSendCodeClick = e => {
    e.preventDefault()

    setIsLoading(true)

    Auth.forgotPassword(email)
      .then(() => {
        setIsLoading(false)
        setCodeSent(true)
      })
      .catch(err => {
        setIsLoading(false)
        dispatch(
          Alert({
            error: t('resetPassword.errors.sendFailed', {
              message: err.message,
            }),
          })
        )
      })
  }

  const handleConfirmClick = async e => {
    e.preventDefault()

    setIsLoading(true)
    setIsSigningIn(false)

    try {
      await Auth.forgotPasswordSubmit(email, confirmationCode, password)

      try {
        setIsSigningIn(true)
        const loginInfo = await Auth.signIn(email, password)

        if (loginInfo.signInUserSession != null) {
          const userPayload = loginInfo.signInUserSession.idToken.payload
          const accessToken = loginInfo.signInUserSession.accessToken.jwtToken
          await props.handleLoginAttempt(true, userPayload, {
            accessToken,
            password,
          })
          navigate('/')
          return
        }

        setIsLoading(false)
        setIsSigningIn(false)
        setConfirmed(true)
      } catch (err) {
        console.error('AutoLoginAfterPasswordReset', err)
        dispatch(
          Alert({
            success: t('resetPassword.success.passwordResetLoginAgain'),
          })
        )
        setIsLoading(false)
        setIsSigningIn(false)
        setConfirmed(true)
      }
    } catch (err) {
      setIsLoading(false)
      setIsSigningIn(false)
      dispatch(
        Alert({
          error: t('resetPassword.errors.resetFailed', {
            message: err.message,
          }),
        })
      )
    }
  }

  const handleSendCodeClickEnter = e => {
    if (e.key === 'Enter') {
      e.preventDefault()
      e.stopPropagation()
      handleSendCodeClick(e)
    }
  }

  const handleConfirmClickEnter = e => {
    if (e.key === 'Enter') {
      e.preventDefault()
      e.stopPropagation()
      handleConfirmClick(e)
    }
  }

  const renderRequestCodeForm = () => {
    return (
      <>
        <DrkTrimMainLogo
          style={{ marginBottom: '54px' }}
          role='img'
          aria-label='MyAssetMap Logo'
        />
        <label
          htmlFor='reset-email'
          className={appScss['input-notched']}
          style={{ marginBottom: '24px' }}
        >
          <span>{t('resetPassword.email')}</span>
          <input
            id='reset-email'
            onChange={e => setEmail(e.target.value)}
            onKeyDown={handleSendCodeClickEnter}
            value={email}
            type='email'
            autoComplete='email'
            inputMode='email'
            style={{ textAlign: 'left' }}
          />
        </label>
        <button
          type='button'
          onClick={handleSendCodeClick}
          disabled={isLoading || !validateCodeForm}
          className={[
            scss['login-submit-button'],
            scss[isLoading ? 'login-submit-button-loading' : ''],
          ].join(' ')}
        >
          {isLoading
            ? t('resetPassword.sending')
            : t('resetPassword.sendConfirmation')}
        </button>
        <Link
          className={scss['login-link']}
          style={{ marginTop: '12px' }}
          to='/login'
        >
          {t('resetPassword.backToLogin')}
        </Link>
      </>
    )
  }

  const renderConfirmationForm = () => {
    return (
      <>
        <DrkTrimMainLogo
          style={{ marginBottom: '54px' }}
          role='img'
          aria-label='MyAssetMap Logo'
        />
        <span
          className={scss['login-helper-text']}
          style={{ marginBottom: '17px' }}
        >
          {t('resetPassword.checkEmail', { email })}
        </span>

        <InputOTP
          autoFocus
          inverse
          maxLength={6}
          pattern={REGEXP_ONLY_DIGITS}
          value={confirmationCode || ''}
          onChange={setConfirmationCode}
          containerClassName='mt-1 w-full'
        >
          <InputOTPGroup className='w-full justify-between'>
            {[0, 1, 2, 3, 4, 5].map(slotIndex => (
              <InputOTPSlot key={slotIndex} index={slotIndex} />
            ))}
          </InputOTPGroup>
        </InputOTP>
        <label
          htmlFor='reset-password'
          className={appScss['input-notched']}
          style={{ marginTop: '32px' }}
        >
          <span>{t('resetPassword.newPassword')}</span>
          <input
            id='reset-password'
            onChange={e => setPassword(e.target.value)}
            onKeyDown={handleConfirmClickEnter}
            value={password}
            type='password'
            style={{ textAlign: 'left' }}
          />
        </label>
        <label
          htmlFor='reset-password-confirm'
          className={appScss['input-notched']}
          style={{ margin: '32px 0 24px 0' }}
        >
          <span>{t('resetPassword.confirmPassword')}</span>
          <input
            id='reset-password-confirm'
            onChange={e => setConfirmPassword(e.target.value)}
            onKeyDown={handleConfirmClickEnter}
            value={confirmPassword}
            type='password'
          />
        </label>
        <button
          type='button'
          onClick={handleConfirmClick}
          disabled={isLoading || !validateResetForm}
          className={[
            scss['login-submit-button'],
            scss[isLoading ? 'login-submit-button-loading' : ''],
          ].join(' ')}
        >
          {isLoading
            ? isSigningIn
              ? t('resetPassword.signingIn')
              : t('resetPassword.confirming')
            : t('resetPassword.confirm')}
        </button>
        <Link
          className={scss['login-link']}
          style={{ marginTop: '12px' }}
          to='/login'
        >
          {t('resetPassword.backToLogin')}
        </Link>
      </>
    )
  }

  const renderSuccessMessage = () => {
    return (
      <>
        <span
          className={scss['login-heading']}
          style={{ marginBottom: '12px' }}
        >
          {t('resetPassword.success.title')}
        </span>
        <div
          className={scss['login-success-icon']}
          style={{ marginBottom: '17px' }}
        >
          <Icon icon={('fal', 'check')} />
        </div>
        <span
          className={[
            scss['login-helper-text'],
            appScss['text-align-center'],
          ].join(' ')}
          style={{ marginBottom: '27px' }}
        >
          {t('resetPassword.success.loginAgainMessage')}
        </span>
        <Link className={scss['login-link-button']} to={loginPath}>
          {t('resetPassword.success.login')}
        </Link>
      </>
    )
  }

  return (
    <div
      className={[
        scss['login-container'],
        confirmed && scss['login-success-container'],
        confirmed && scss['flex-align-center'],
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {!codeSent
        ? renderRequestCodeForm()
        : !confirmed
          ? renderConfirmationForm()
          : renderSuccessMessage()}
    </div>
  )
}
